import { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { Field } from "formik";
import * as Yup from "yup";
import get from "lodash/get";
import AceEditorField from "../../molecules/AceEditor/AceEditorField";
import BackgroundTaskInputs from "./DataModelsDrawers/BackgroundTaskInputs";
import { INTERFACES } from "./drawers.constants";
import { addImageIcon } from "../../../assets/icons";
import {
  getFormattedDate,
  getISOFormattedDate,
  formattedFileName,
} from "../../../helpers";
import { FieldsGrid, NumberField } from "../../molecules";
import Repeater from "../../molecules/Repeater/Repeater";
import {
  DATE_FORMATS,
  REGEX,
  INDEX,
  EMPTY_ARRAY,
  EMPTY_STRING,
  DEPLOY_STATUSES,
  DEPLOY_PIPELINE_STEPS,
  DEPLOY,
} from "../../../constants";

const {
  CHECKBOX,
  INPUT,
  TEXT_AREA,
  SELECT,
  FILE,
  DATE_TIME,
  ACE_EDITOR,
  NUMBER,
  INPUT_FORM,
  BOOLEAN,
  RADIO,
  KEY_VALUE_FORM,
  REPEATER_FIELDS,
  IMAGE,
  EMAIL,
  FLOAT_NUMBER,
  JSON_TYPE,
} = INTERFACES;
const { FILE_NAME_FROM_URL } = REGEX;
const { SECOND } = INDEX;
const { DD_MM_YYYY } = DATE_FORMATS;
const STRING = "string";
const OPTIONS_FIELD_NAME = "meta.options";
const HTTPS = "https";
const GCP = "GCP";
const { COMPLETED, SUCCESS, QUEUED, IN_PROGRESS, FAILURE, CANCELLED,SKIPPED, PENDING } =
  DEPLOY_STATUSES;
const { FRONTEND, BACKEND } = DEPLOY;
const {
  PUSHING_TO_GITHUB,
  GENERATING_CODE,
  AWS_DEPLOYMENT,
  GCP_DEPLOYMENT,
  DEPLOYMENT,
  CI_CD_PIPELINE,
  BUILD,
} = DEPLOY_PIPELINE_STEPS;
const INITIAL_MESSAGE = "Your Request has been accepted by the Backend";

const getFileName = (uploadedFile) => {
  const isURL =
    typeof uploadedFile === STRING && uploadedFile.startsWith(HTTPS);

  if (isURL) {
    const uploadedFileName = uploadedFile.match(FILE_NAME_FROM_URL)[SECOND];
    const decodedURI = decodeURIComponent(uploadedFileName);
    return formattedFileName(decodedURI);
  }

  const fileName = uploadedFile["name"] || JSON.parse(uploadedFile)["name"];
  return formattedFileName(fileName);
};

const CustomInput = forwardRef(function CustomInput(
  { value, onClick, onChange },
  ref
) {
  return (
    <input
      value={value}
      className="form-control pl-8 bg-[url('./assets/icons/datePicker.svg')] bg-no-repeat focus:outline-blue-600 focus:ring-blue-600 bg-[center_left_10px]"
      placeholder={"Pick Date"}
      onClick={onClick}
      onChange={onChange}
      ref={ref}
    />
  );
});

export const renderField = (props) => {
  const {
    value,
    name,
    label,
    handleBlur,
    handleChange,
    spanText = EMPTY_STRING,
    options = EMPTY_ARRAY,
    interfaceType,
    touched,
    errors,
    shouldShowValidationMessage = false,
    disabled,
    isReadonly = false,
    setFieldValue,
    setFieldTouched,
    selectedChoices,
    type,
    shouldUpdateType,
  } = props;

  const isTouched = get(touched, name, EMPTY_STRING);
  const errorMessage = get(errors, name, EMPTY_STRING);
  const optionsErrorMessage = get(errors, OPTIONS_FIELD_NAME, EMPTY_STRING);
  const isRepeaterFieldTouched = get(touched, OPTIONS_FIELD_NAME, EMPTY_STRING);
  const repeaterFieldErrorMessage = optionsErrorMessage || errorMessage;

  switch (interfaceType) {
    case INPUT:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
            {label}
          </label>
          <Field
            type="text"
            name={name}
            value={value}
            className="form-control"
            placeholder={label}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={isReadonly}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case EMAIL:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
            {label}
          </label>
          <Field
            type="email"
            name={name}
            value={value}
            className="form-control"
            placeholder={label}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={isReadonly}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case TEXT_AREA:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
            {label}
          </label>
          <Field
            as="textarea"
            name={name}
            rows={1}
            className="form-control"
            placeholder={label}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case NUMBER:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block">
            {label}
          </label>
          <Field
            type="number"
            name={name}
            className="form-control"
            placeholder={label}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case FLOAT_NUMBER:
      return (
        <NumberField
          value={value}
          name={name}
          label={label}
          handleBlur={handleBlur}
          handleChange={handleChange}
          options={options}
          disabled={disabled}
          type={type}
          shouldUpdateType={shouldUpdateType}
        />
      );
    case JSON_TYPE:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block">
            {label}
          </label>
          <Field
            as="textarea"
            name={name}
            value={value}
            handleBlur={handleBlur}
            handleChange={handleChange}
            type={type}
            className="w-full form-control"
            placeholder={label}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case CHECKBOX:
      return (
        <label className="text-gray-300 flex mb-5 capitalize">
          <Field
            type="checkbox"
            name={name}
            data-testid={name}
            checked={value}
            className="text-indigo-600 !rounded focus:ring-0 focus:outline-indigo-300 cursor-pointer"
            disabled={disabled}
            onChange={handleChange}
          />
          <div className="ml-3">
            <h6 className="text-sm font-medium leading-4 mb-1">{label}</h6>
            {spanText && (
              <span className="text-sm font-light leading-5 block">
                {spanText}
              </span>
            )}
          </div>
        </label>
      );
    case SELECT:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block capitalize">
            {label}
          </label>
          <Field
            as="select"
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            disabled={disabled}
          >
            <option value={EMPTY_STRING} disabled>
              Select a value
            </option>
            {options.map((option) =>
              typeof option === STRING ? (
                <option key={option} value={option}>
                  {option}
                </option>
              ) : (
                <option key={option.value.toString()} value={option.value}>
                  {option.name || option.value}
                </option>
              )
            )}
          </Field>
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-red-500 font-medium text-sm">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case IMAGE:
    case FILE:
      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block">
            {label}
          </label>
          <label>
            <div className="border-2 border-gray-300 border-dashed rounded-md p-7 grid place-content-center">
              {value ? (
                <div
                  className="text-gray-50"
                  data-testid={`selectedfile-${name}`}
                >
                  <img
                    src={addImageIcon}
                    width="48"
                    className="mx-auto"
                    alt="add-icon"
                  />
                  {getFileName(value)}
                </div>
              ) : (
                <span>
                  <div className="text-center">
                    <div className="icon mb-1">
                      <img
                        src={addImageIcon}
                        width="48"
                        className="mx-auto"
                        alt="add-icon"
                      />
                    </div>
                    <div className="mb-1">
                      <div className="text-indigo-200 hover:opacity-90 hover:underline text-sm inline">
                        Upload a {interfaceType}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {interfaceType === FILE
                        ? "File up to 10MB"
                        : "PNG, JPG, GIF up to 10MB"}
                    </p>
                  </div>
                </span>
              )}
              {!disabled && (
                <Field
                  type="file"
                  name={name}
                  data-testid={name}
                  className="hidden"
                  value={EMPTY_STRING}
                  onChange={handleChange}
                  {...(interfaceType === IMAGE && {
                    accept: selectedChoices,
                  })}
                />
              )}
            </div>
          </label>
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case ACE_EDITOR:
      return (
        <>
          <AceEditorField
            value={value}
            handleChange={handleChange}
            name={name}
            label={label}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </>
      );
    case INPUT_FORM:
      return (
        <>
          <BackgroundTaskInputs
            name={name}
            label={label}
            handleChange={handleChange}
            options={options}
            values={value}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </>
      );
    case DATE_TIME: {
      const updateDate = (date) =>
        setFieldValue(`${name}`, getISOFormattedDate(date));

      if (!value) updateDate(new Date());

      return (
        <div className="form-group">
          <label className="text-sm font-medium text-gray-50 mb-1 block">
            {label}
          </label>
          <div className="flex justify-start items-center relative">
            <DatePicker
              name={name}
              value={getFormattedDate(value, DD_MM_YYYY)}
              selected={new Date(value)}
              Clearable
              customInput={<CustomInput />}
              onChange={updateDate}
            />
          </div>
        </div>
      );
    }
    case BOOLEAN:
      return (
        <div className="form-group">
          <h6 className="text-sm font-medium text-gray-50 mb-5 capitalize">
            {label}
          </h6>
          <label className="inline-flex cursor-pointer mr-auto md:mr-0 ml-auto mb-5">
            <Field
              type="checkbox"
              name={name}
              checked={value}
              role="switch"
              className="hidden peer"
              disabled={disabled}
            />
            <div className="w-11 h-6 relative bg-gray-700 rounded-full peer-checked:after:translate-x-full peer-checked:after:border-white  after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-emerald-500"></div>
            <div className="ml-3 text-sm">
              <p className="font-medium text-gray-50 dark:text-gray-300">
                {value ? "True" : "False"}
              </p>
            </div>
          </label>
        </div>
      );
    case RADIO:
      return (
        <div className="form-group">
          <div
            id="radio-group"
            className="text-sm font-medium text-gray-50 mb-1 block"
          >
            {label}
          </div>
          <div className="flex flex-row gap-3">
            {options.map((option) => (
              <label
                className="text-sm font-medium text-gray-50 mb-1"
                key={option.value}
              >
                <Field
                  type="radio"
                  name={name}
                  value={option.value}
                  className="mr-2"
                  disabled={disabled}
                  onClick={handleChange}
                />
                {option.name}
              </label>
            ))}
          </div>
        </div>
      );
    case KEY_VALUE_FORM:
      return (
        <div className="text-gray-50">
          <FieldsGrid
            name={name}
            values={value}
            label={label}
            fields={[
              { name: "key", type: "text" },
              { name: "value", type: "text" },
            ]}
            styleClasses={{ textFields: "flex grid grid-cols-2 gap-2 my-1" }}
          />
          {shouldShowValidationMessage && isTouched && errorMessage && (
            <div className="text-sm font-medium text-red-500">
              {errorMessage}
            </div>
          )}
        </div>
      );
    case REPEATER_FIELDS:
      return (
        <>
          <Repeater
            fieldType={interfaceType}
            fieldName={name}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
          />
          {shouldShowValidationMessage &&
            isRepeaterFieldTouched &&
            repeaterFieldErrorMessage && (
              <div className="text-sm font-medium text-red-500">
                {repeaterFieldErrorMessage}
              </div>
            )}
        </>
      );
    default:
      return null;
  }
};

export const getValidationSchema = (fields) => {
  let schema = {};
  fields.forEach((field) => {
    if (
      field.meta.isRequired &&
      !field.meta.isKey &&
      (field.meta.hidden || field.meta.readOnly)
    )
      schema = {
        ...schema,
        [field.schema.key]: Yup.string().required(
          `${field.name} (${
            field.meta.readOnly ? "Read Only" : "Hidden"
          }): Value is required.`
        ),
      };
    else if (field.meta.isRequired && !field.meta.isKey && !field.meta.hidden)
      schema = {
        ...schema,
        [field.schema.key]:
          field.meta.interface === FILE
            ? Yup.object().required(`${field.name} is required.`)
            : Yup.string().required(`${field.name} is required.`),
      };
  });
  const validationSchema = Yup.object({ ...schema });
  return validationSchema;
};

export const getInitialValues = (fields) => {
  let values = {};
  fields.forEach((field) => {
    values = {
      ...values,
      [field.schema.key]: field.schema.defaultValue,
    };
  });
  return values;
};

export const getFieldType = (fieldKey, interfaceType) => {
  const DROPDOWN_FIELDS = [
    "referenceTable",
    "referenceTableId",
    "referenceTableColumn",
  ];
  const TOGGLE_FIELDS = ["toggleValue"];
  const CHECKBOX_FIELDS = [
    "isRequired",
    "hidden",
    "readOnly",
    "hideTrueValues",
    "opacity",
  ];
  const RADIO_FIELDS = ["dropdownType"];

  const { CHECKBOX, INPUT } = INTERFACES;
  const isCheckBox = CHECKBOX_FIELDS.includes(fieldKey);
  const isDropDown = DROPDOWN_FIELDS.includes(fieldKey);
  const isToggle = TOGGLE_FIELDS.includes(fieldKey);
  const isRadio = RADIO_FIELDS.includes(fieldKey);
  if (isCheckBox) return CHECKBOX;
  else if (isDropDown) return SELECT;
  else if (isToggle) return BOOLEAN;
  else if (isRadio) return RADIO;
  else if (fieldKey.includes("name")) return INPUT;
  else return interfaceType;
};

export const getFieldLabel = (fieldName) => {
  switch (fieldName) {
    case "readOnly":
      return "Read Only";
    case "isRequired":
      return "Required";
    case "defaultValue":
      return "Default Value";
    case "itemsPerPage":
      return "Items Per Page";
    case "referenceTableId":
      return "Reference Table";
    case "dropdownType":
      return "Dropdown Type";
    default:
      return fieldName;
  }
};

export const getSubStepStatus = (status, conclusion) => {
  switch (status) {
    case COMPLETED:
      return conclusion === SUCCESS || conclusion === SKIPPED ? SUCCESS : FAILURE;
    case IN_PROGRESS:
      return IN_PROGRESS;
    case QUEUED:
    case PENDING:  
      return QUEUED;
    default:
      return FAILURE;
  }
};

export const getStepStatus = (status) => {
  switch (status) {
    case SUCCESS:
      return SUCCESS;
    case FAILURE:
    case CANCELLED:
      return FAILURE;
    default:
      return QUEUED;
  }
};

export const getPipelineSteps = (type, logs, cloudService, selectedApp) => {
  if (type === FRONTEND) {
    return [
      {
        name: INITIAL_MESSAGE,
        status: "success",
      },
      {
        name: GENERATING_CODE,
        status: logs?.CREATE_PROJECT?.status,
        steps: [],
      },
      {
        name: BUILD,
        status: logs?.BUILD?.status,
        steps: logs?.BUILD?.steps,
      },
      {
        name: DEPLOYMENT,
        status: getStepStatus(logs?.BUILD?.status),
        steps: [],
      },
    ];
  } else if (type === BACKEND) {
    if (cloudService === GCP)
      return [
        {
          name: INITIAL_MESSAGE,
          status: "success",
        },
        {
          name: GENERATING_CODE,
          status: get(logs, "CREATE_PROJECT.status", EMPTY_STRING),
          steps: [],
        },
        {
          name: PUSHING_TO_GITHUB,
          status: getStepStatus(
            get(logs, "CREATE_PROJECT.status", EMPTY_STRING)
          ),
          steps: get(logs, "CREATE_PROJECT.steps", EMPTY_ARRAY),
        },
        {
          name: CI_CD_PIPELINE,
          status:
            get(logs, "CI.status") === IN_PROGRESS
              ? get(logs, "CI.status", EMPTY_STRING)
              : get(logs, "CD.status", EMPTY_STRING),
          steps: get(logs, "CI.steps", EMPTY_ARRAY) &&
            get(logs, "CD.steps", EMPTY_ARRAY) && [
              ...get(logs, "CI.steps", EMPTY_ARRAY),
              ...get(logs, "CD.steps", EMPTY_ARRAY),
            ],
        },
        {
          name: GCP_DEPLOYMENT,
          status: get(logs, "GCP.status", EMPTY_STRING),
          steps: get(logs, "GCP.steps", EMPTY_ARRAY),
        },
        {
          name: DEPLOYMENT,
          status: getStepStatus(get(logs, "GCP.status", EMPTY_STRING)),
          steps: [],
        },
      ];
    else
      return [
        {
          name:INITIAL_MESSAGE,
          status: "success",
        },
        {
          name: GENERATING_CODE,
          status: logs?.CREATE_PROJECT?.status,
          steps: [],
        },
        {
          name: PUSHING_TO_GITHUB,
          status: getStepStatus(logs?.CREATE_PROJECT?.status),
          steps: logs?.CREATE_PROJECT?.steps,
        },
        {
          name: CI_CD_PIPELINE,
          status:
            logs?.CI?.status === IN_PROGRESS
              ? logs?.CI?.status
              : logs?.CD?.status,
          steps: logs?.CI?.steps &&
            logs?.CD?.steps && [...logs.CI.steps, ...logs.CD.steps],
        },
        {
          name: AWS_DEPLOYMENT,
          status: logs?.AWS?.status,
          steps: logs?.AWS?.steps,
        },
        {
          name: DEPLOYMENT,
          status: getStepStatus(logs?.AWS?.status),
          steps: [],
        },
      ];
  }
};
